import { RubyGemMark } from '../../components/RubyGemMark';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Truck, AlertTriangle, RefreshCw, ArrowLeft, ArrowRight, Package, PackageCheck,
  Clock, MapPin, Copy, Check,
} from 'lucide-react';
import { api } from '../../services/apiService';
import { toastService } from '../../services/toastService';
import {
  hardwareApi, orderBillingLabel, orderMoney, shortDate,
  ORDER_STATUS_CHIP, ORDER_STATUS_LABEL,
  type DeviceOrderRow, type DeviceUnitRow, type DeviceBundleRow, type DeviceModelRow,
} from '../../services/hardwareApi';
import OrderDetail from '../pos/hardware/OrderDetail';
import { InventoryPage } from './InventoryChrome';
import Barcode from '../../components/Barcode';
import { useSoftwareMonthlyByTenant } from './PlanAndPaymentPanels';
import { merchantApi, MERCHANT_STATUS_CHIP, MERCHANT_STATUS_LABEL, type MerchantApplicationView } from '../../services/merchantApi';
import { needsTerminalId, activeSheetFor, displayOrderNumber } from '@platform/shared-types';
import { RubyLoader } from '../../components/RubyLoader';

/**
 * ORDERS — mockup screen 4, the pipeline-first view, PLUS (pos-config
 * cleanup) the per-device fulfilment detail that used to only be reachable
 * through the pos-config "Orders" tab (`OrdersTab` → `OrderDetail`). That tab
 * is gone; `?open=<orderId>` drills into the SAME `OrderDetail` component
 * from here, so nothing that could move a device through the pipeline was
 * lost — it just lives at one URL now instead of two.
 *
 * The Agreement column renders the order's real `envelopeStatus` (stamped by
 * billing-service, a display cache — see AGREEMENT_CHIP/AGREEMENT_LABEL
 * below), with an honest "—" only when the order carries no envelope at all
 * (W5 review I8).
 */

// W5 review I8 — mockup screen 4's "✍ Signed / Sent, unsigned" column. Every
// terminal/non-terminal envelope status documents-service can report
// (`ENVELOPE_STATUSES`, shared-types/documentSigning.ts), mapped to how an
// operator should read it at a glance. This is a DISPLAY read of the
// order's cached `envelopeStatus` — the packing gate itself always does a
// live read, never this cache.
const AGREEMENT_CHIP: Record<string, string> = {
  executed: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  sent: 'bg-amber-100 text-amber-700 border-amber-200',
  partially_signed: 'bg-amber-100 text-amber-700 border-amber-200',
  draft: 'bg-stone-100 text-stone-500 border-stone-200',
  declined: 'bg-red-100 text-red-700 border-red-200',
  voided: 'bg-red-100 text-red-700 border-red-200',
  expired: 'bg-red-100 text-red-700 border-red-200',
};
const AGREEMENT_LABEL: Record<string, string> = {
  executed: '✍ Signed',
  sent: 'Sent, unsigned',
  partially_signed: 'Partially signed',
  draft: 'Draft',
  declined: 'Declined',
  voided: 'Voided',
  expired: 'Expired',
};

/**
 * THE PIPELINE, AS FIVE STAGES A PARCEL ACTUALLY PASSES THROUGH.
 *
 * Each carries its own icon and accent so the strip reads as a journey rather
 * than five identical grey boxes — the stage a reader is looking for is found
 * by shape and colour before the label is read at all. The `accent` is used for
 * the count, the icon tile and the rule under an active stage; nothing else is
 * painted with it, so "which stage" and "how many" never compete.
 */
const PIPELINE: {
  id: string;
  label: string;
  Icon: React.FC<any>;
  accent: string;
  tile: string;
  match: (s: string) => boolean;
}[] = [
  {
    id: 'awaiting', label: 'Awaiting', Icon: Clock, accent: '#B45309', tile: 'bg-amber-100 text-amber-700',
    match: (s) => s === 'awaiting' || s === 'processing' || s === 'backordered' || s === 'on_hold',
  },
  { id: 'packing', label: 'Packing', Icon: Package, accent: '#4338CA', tile: 'bg-indigo-100 text-indigo-700', match: (s) => s === 'packing' },
  { id: 'shipped', label: 'Shipped', Icon: Truck, accent: '#0E7490', tile: 'bg-cyan-100 text-cyan-700', match: (s) => s === 'shipped' },
  { id: 'delivered', label: 'Delivered', Icon: PackageCheck, accent: '#047857', tile: 'bg-emerald-100 text-emerald-700', match: (s) => s === 'delivered' },
  { id: 'deployed', label: 'Deployed', Icon: MapPin, accent: '#9F1239', tile: 'bg-rose-100 text-rose-700', match: (s) => s === 'deployed' },
];

const money = (cents: number) => `$${(cents / 100).toFixed(2)}`;

/**
 * ===========================================================================
 * THE ORDER NUMBER, AS A TICKET STUB
 * ===========================================================================
 *
 * This screen used to print `RB-1 004 382` in red over the raw
 * `ord_8684fffa5703dcff22936724` — a UUID nobody reads, given a whole line of
 * its own directly under the number that people DO read, in the first column,
 * on every row. It made the most important identifier on the screen the second
 * thing in a pair of identifiers.
 *
 * So: the human number leads, in a stub with the bars under it, and it looks
 * like the order it is. The barcode is real Code 128 (`utils/code128.ts`, the
 * same encoder the Rollo label printer uses), so a warehouse scanner reads it
 * off the screen exactly as it reads it off a printed pick sheet.
 *
 * THE UUID IS STILL HERE, because it is the key every unit, envelope and
 * invoice references and a claim two years from now will quote it. It is a
 * copy button and a tooltip rather than a printed line — one click to get it
 * onto a clipboard, nought lines of the operator's attention when they do not
 * want it.
 */
/**
 * A LEGACY ORDER'S SHORT REFERENCE.
 *
 * `orderNo` is minted at order creation (hardware-service `orders.ts`), so an
 * order without one predates the field — and this screen has to show something
 * for it that is not twenty-eight characters of hex.
 *
 * IT DOES NOT INVENT AN ORDER NUMBER. A fabricated `RB-…` would carry a valid
 * check digit, look exactly like a real one, and match nothing the server has
 * ever heard of — an operator would read it down the phone and get nowhere.
 * This is deliberately shaped so it can never be mistaken for one: a `#` and
 * hex, no prefix, no grouping. The FULL id is still what the barcode encodes
 * and what the copy button yields, so nothing operational rests on the short
 * form.
 */
const shortRef = (orderId: string): string =>
  `#${String(orderId).replace(/^ord_/, '').slice(0, 8).toUpperCase()}`;

/**
 * ===========================================================================
 * THE ORDER NUMBER, AS A TICKET STUB
 * ===========================================================================
 *
 * This screen used to print `RB-1 004 382` in red over the raw
 * `ord_8684fffa5703dcff22936724` — a UUID nobody reads, given a whole line of
 * its own directly under the number that people DO read, in the first column,
 * on every row. It made the most important identifier on the screen the second
 * thing in a pair of identifiers, and on a legacy order with no `orderNo` it
 * printed the UUID and nothing else.
 *
 * So: the identifier leads, in a stub with bars under it, and it looks like the
 * order it is. The barcode is real Code 128 (`utils/code128.ts`, the encoder
 * the Rollo label printer shares), so a warehouse scanner reads it off the
 * screen exactly as it reads it off a printed pick sheet.
 *
 * THE BARCODE CARRIES THE ORDER NUMBER, AND ONLY THE ORDER NUMBER.
 *
 * Never the short reference — a barcode that scans to something no lookup
 * accepts is worse than no barcode. And never the `ord_…` id either, which was
 * tried: twenty-eight characters is a 343-module symbol, ~309px wide in a
 * table cell that is 160px, and squeezed to fit it is under half a pixel per
 * module and scans as nothing. A legacy order therefore gets NO bars, which is
 * the honest answer — there is nothing short and scannable to encode, and a
 * decorative smear of lines pretending otherwise helps nobody.
 *
 * THE UUID IS STILL HERE, because it is the key every unit, envelope and
 * invoice references and a claim two years from now will quote it. It is a
 * copy button and a tooltip rather than a printed line — one click to get it
 * onto a clipboard, nought lines of the operator's attention when they do not
 * want it.
 */
const OrderStub: React.FC<{ order: DeviceOrderRow; onCopy: (id: string) => void; copied: boolean }> = ({
  order, onCopy, copied,
}) => {
  const numbered = Boolean(order.orderNo);
  const label = numbered ? displayOrderNumber(order.orderNo!) : shortRef(order.orderId);

  return (
    <div className="rounded-lg border border-stone-200 bg-white px-3 py-2 inline-block min-w-[10rem]">
      <div className="flex items-center gap-1.5">
        <span
          className={`font-mono font-bold tracking-tight ${
            numbered ? 'text-[13px] text-stone-900' : 'text-[12.5px] text-stone-600'
          }`}
        >
          {label}
        </span>
        <button
          onClick={(e) => { e.stopPropagation(); onCopy(order.orderId); }}
          title={`Copy ${order.orderId}`}
          aria-label={`Copy the full id for order ${label}`}
          className="text-stone-300 hover:text-stone-700 shrink-0"
        >
          {copied ? <Check size={12} className="text-emerald-600" /> : <Copy size={12} />}
        </button>
      </div>

      {/* The RAW number, not `displayOrderNumber`'s grouped form — the spaces
          are for reading aloud and would scan back as a string no lookup
          accepts. */}
      {numbered && (
        <Barcode value={order.orderNo!} height={22} moduleWidth={0.9} className="mt-1 text-stone-900 block" />
      )}

      {/* Said plainly, and only where it is true: this order has no order
          number, so the reference above is not one and cannot be quoted as
          one. Every order placed since numbering shipped has one. */}
      {!numbered && (
        <div className="mt-1 text-[9px] uppercase tracking-wide text-stone-400">no order no. · pre-numbering</div>
      )}
    </div>
  );
};

const CARD_CAP = 4;

const OrdersPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // `?open=<orderId>` drills into OrderDetail — set by this page's own rows,
  // by other pages linking here (Pending configuration, Inventory Hub's unit
  // rows), and by the old `/pos-config?tab=orders` redirect.
  const open = searchParams.get('open');
  const [orders, setOrders] = useState<DeviceOrderRow[]>([]);
  const [units, setUnits] = useState<DeviceUnitRow[]>([]);
  const [models, setModels] = useState<DeviceModelRow[]>([]);
  const [bundles, setBundles] = useState<DeviceBundleRow[]>([]);
  const [tenantNames, setTenantNames] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * The stage strip FILTERS, rather than only counting.
   *
   * Five boxes stating five numbers over a list of every order made the reader
   * do the filtering by eye. `null` is "all orders", which is what the strip
   * reads as when nothing is picked.
   */
  const [stage, setStage] = useState<string | null>(null);
  /** The order whose id was last copied, for the tick on its stub. */
  const [copied, setCopied] = useState<string | null>(null);
  // The merchant applications behind the Merchant column. Additive: a failure
  // to read them must never hide the orders themselves, which is the whole
  // point of this screen.
  const [apps, setApps] = useState<Record<string, MerchantApplicationView>>({});

  const load = useCallback(async () => {
    setError(null);
    try {
      const [o, u, m, b] = await Promise.all([
        hardwareApi.listOrders(),
        hardwareApi.listUnits(),
        // Reference data for OrderDetail's device picker — a failure to read
        // them must not hide the orders themselves.
        hardwareApi.listModels().catch(() => ({ items: [] as DeviceModelRow[] })),
        hardwareApi.listBundles().catch(() => ({ items: [] as DeviceBundleRow[] })),
      ]);
      setOrders(o.items || []);
      setUnits(u.items || []);
      setModels(m.items || []);
      setBundles(b.items || []);
    } catch (e: any) {
      setError(e?.message || 'Failed to load orders');
    }
    /*
      The merchant applications behind the Merchant column.

      Read from the QUEUE, in one call, rather than one per order: the queue
      already carries every open application across every tenant, and a
      per-row fetch would be forty requests to render a table. Keyed by
      locationId, because that is what an order carries.

      A failure here leaves the column blank and the orders visible. The
      column is information; the pipeline is the job.
    */
    try {
      const { applications } = await merchantApi.listApplications();
      const byLocation: Record<string, MerchantApplicationView> = {};
      for (const a of applications || []) byLocation[a.locationId] = a;
      setApps(byLocation);
    } catch { /* the Merchant column stays blank rather than the page failing */ }

    try {
      const tenants = await api.tenants.getAll();
      const names: Record<string, string> = {};
      for (const t of tenants || []) names[t.id] = t.name || t.businessName || t.id;
      setTenantNames(names);
    } catch { /* fall back to raw tenantId */ }
  }, []);

  useEffect(() => { (async () => { setLoading(true); await load(); setLoading(false); })(); }, [load]);

  const refresh = async () => { setRefreshing(true); await load(); setRefreshing(false); };

  const openOrder = (orderId: string) => setSearchParams((prev) => {
    const p = new URLSearchParams(prev);
    p.set('open', orderId);
    return p;
  });
  const closeOrder = () => setSearchParams((prev) => {
    const p = new URLSearchParams(prev);
    p.delete('open');
    return p;
  });

  const unitsByOrder = useMemo(() => {
    const map = new Map<string, DeviceUnitRow[]>();
    for (const u of units) {
      if (!u.orderId) continue;
      if (!map.has(u.orderId)) map.set(u.orderId, []);
      map.get(u.orderId)!.push(u);
    }
    return map;
  }, [units]);

  const nameOf = (tenantId: string) => tenantNames[tenantId] || tenantId;

  const columns = useMemo(
    () => PIPELINE.map((col) => ({ ...col, orders: orders.filter((o) => col.match(o.status)) })),
    [orders],
  );

  /** What the table shows: the picked stage, or everything. */
  const visible = useMemo(() => {
    if (!stage) return orders;
    const col = PIPELINE.find((c) => c.id === stage);
    return col ? orders.filter((o) => col.match(o.status)) : orders;
  }, [orders, stage]);

  /**
   * THE MONTHLY SUBSCRIPTION, PER ORDER — both halves of it.
   *
   * The Financing column quoted `orderBillingLabel`, which is the HARDWARE
   * alone. On a screen about hardware that number reads as the customer's
   * subscription and it is not: the software they are on is the larger half of
   * most of these bills. Same fix, and the same source, as the order detail's
   * own header.
   *
   * A tenant missing from `softwareByTenant` is one we could not price — never
   * one paying nothing — so those rows show the hardware and say so, rather
   * than adding to a zero and quoting a total that is short by the software.
   */
  const softwareByTenant = useSoftwareMonthlyByTenant(
    useMemo(() => orders.map((o) => o.tenantId), [orders]),
  );

  const copyId = (orderId: string) => {
    navigator.clipboard?.writeText(orderId)
      .then(() => { setCopied(orderId); setTimeout(() => setCopied((c) => (c === orderId ? null : c)), 1600); })
      .catch(() => toastService.error('Could not copy the order id'));
  };

  if (loading) {
    return (
      <InventoryPage icon={Truck} title="Orders" subtitle="Pipeline of device shipments">
        <RubyLoader variant="page" />
      </InventoryPage>
    );
  }

  // `?open=<orderId>` — the per-device fulfilment detail that used to live
  // one click into the pos-config "Orders" tab. `OrderDetail` fetches its own
  // order/unit data from `orderId` alone, so this works even for an order not
  // (yet) present in this page's own `orders` list.
  if (open) {
    return (
      <InventoryPage
        icon={Truck}
        title="Orders"
        subtitle="Pipeline of device shipments"
        /*
          FULL WIDTH for the detail view only, never the list.

          The stage rail forks into four lanes side by side, and inside the
          1400px cap they only fit two-up — which made the rail twice as tall
          and left the lanes hard to tell apart. The list keeps the cap: long
          table rows really do get harder to read the wider they run.
        */
        wide
        actions={(
          <button
            onClick={closeOrder}
            className="flex items-center gap-2 px-3 py-2 rounded-lg border border-white/10 text-gray-300 hover:text-white hover:bg-white/10 text-sm font-medium min-h-[44px]"
          >
            <ArrowLeft size={15} /> All orders
          </button>
        )}
      >
        <OrderDetail
          orderId={open}
          models={models}
          bundles={bundles}
          tenantName={nameOf(orders.find((o) => o.orderId === open)?.tenantId || '')}
          onChanged={load}
          onClose={closeOrder}
        />
      </InventoryPage>
    );
  }

  return (
    <InventoryPage
      icon={Truck}
      title="Orders"
      subtitle="Pipeline of device shipments"
      actions={(
        <button
          onClick={refresh}
          disabled={refreshing}
          className="flex items-center gap-2 px-3 py-2 rounded-lg border border-white/10 text-gray-300 hover:text-white hover:bg-white/10 text-sm font-medium min-h-[44px] disabled:opacity-50"
        >
          {refreshing ? <RubyGemMark size={15} drawing color="currentColor" /> : <RefreshCw size={15} />} Refresh
        </button>
      )}
    >
      {error && (
        <div className="mb-4 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-3">
          <AlertTriangle size={16} className="mt-0.5 shrink-0 text-red-600" />
          <div className="text-sm text-red-800">{error}</div>
        </div>
      )}

      {/*
        THE PIPELINE STRIP.

        Five grey boxes, each listing raw `ord_…` UUIDs in red over a tenant
        name, is what this was — the ids were unreadable, the boxes were inert,
        and a reader wanting "just the awaiting ones" filtered the table below
        by eye.

        Now each stage is a BUTTON that filters, carries its own icon and
        accent so it is found by shape before it is read, and lists its orders
        by the number a person actually says out loud.
      */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        {columns.map((col) => {
          const active = stage === col.id;
          return (
            <button
              key={col.id}
              type="button"
              aria-pressed={active}
              onClick={() => setStage(active ? null : col.id)}
              className={`text-left rounded-xl border bg-white p-3 transition-shadow ${
                active ? 'border-stone-400 shadow-sm' : 'border-stone-200 hover:border-stone-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <span className={`w-7 h-7 rounded-lg grid place-items-center shrink-0 ${col.tile}`}>
                  <col.Icon size={14} strokeWidth={2.4} />
                </span>
                <span className="text-[11px] font-bold uppercase tracking-wide text-stone-500 truncate">
                  {col.label}
                </span>
                <span
                  className="ml-auto text-lg font-extrabold tabular-nums leading-none"
                  style={{ color: col.orders.length ? col.accent : '#a8a29e' }}
                >
                  {col.orders.length}
                </span>
              </div>

              {/* The active stage is underlined in its own colour — the strip
                  says which slice the table below is showing. */}
              <div className="mt-2 h-[3px] rounded-full" style={{ background: active ? col.accent : '#e7e5e4' }} />

              <div className="mt-2 space-y-1">
                {col.orders.slice(0, CARD_CAP).map((o) => (
                  <div key={o.orderId} className="flex items-baseline gap-1.5 text-[11px]">
                    <span className="font-mono font-semibold text-stone-700 shrink-0">
                      {o.orderNo ? displayOrderNumber(o.orderNo) : shortRef(o.orderId)}
                    </span>
                    <span className="text-stone-400 truncate">{nameOf(o.tenantId)}</span>
                  </div>
                ))}
                {col.orders.length === 0 && <p className="text-[11px] text-stone-300">Nothing here</p>}
                {col.orders.length > CARD_CAP && (
                  <p className="text-[10.5px] text-stone-400">+{col.orders.length - CARD_CAP} more</p>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {stage && (
        <div className="mb-3 flex items-center gap-2 text-[12px] text-stone-600">
          <span>
            Showing <b className="text-stone-900">{visible.length}</b>{' '}
            {PIPELINE.find((c) => c.id === stage)?.label.toLowerCase()} order{visible.length === 1 ? '' : 's'}.
          </span>
          <button onClick={() => setStage(null)} className="font-semibold text-stone-500 underline hover:text-stone-900">
            Show all {orders.length}
          </button>
        </div>
      )}

      <div className="rounded-xl border border-stone-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 border-b border-stone-200">
              <tr className="text-left text-[10px] uppercase tracking-wide text-stone-500">
                <th className="px-4 py-3 font-semibold">Order</th>
                <th className="px-4 py-3 font-semibold">Tenant</th>
                <th className="px-4 py-3 font-semibold">Devices / serials</th>
                {/* Not "Financing": this is what the customer pays every month,
                    software and hardware together. See the cell. */}
                <th className="px-4 py-3 font-semibold">Subscription</th>
                <th className="px-4 py-3 font-semibold">Agreement</th>
                <th className="px-4 py-3 font-semibold">Merchant</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold" />
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {visible.map((o) => {
                const orderUnits = unitsByOrder.get(o.orderId) || [];
                const hardware = orderMoney(o.items);
                const software = softwareByTenant[o.tenantId];
                return (
                  <tr key={o.orderId} className="hover:bg-stone-50 cursor-pointer" onClick={() => openOrder(o.orderId)}>
                    <td className="px-4 py-3 align-top">
                      <OrderStub order={o} onCopy={copyId} copied={copied === o.orderId} />
                    </td>
                    <td className="px-4 py-3 align-top">
                      <div className="text-stone-800">{nameOf(o.tenantId)}</div>
                      <div className="text-[11px] text-stone-400 mt-0.5">placed {shortDate(o.createdAt)}</div>
                    </td>
                    <td className="px-4 py-3 text-stone-600 text-xs max-w-[16rem]">
                      {orderUnits.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {orderUnits.map((u) => (
                            <button
                              key={u.unitId}
                              onClick={(e) => { e.stopPropagation(); navigate(`/inventory/units/${encodeURIComponent(u.unitId)}`); }}
                              className="font-mono text-[11px] text-red-500 hover:underline"
                              title={`Open ${u.unitId}`}
                            >
                              {u.serialNumber || u.unitId}
                            </button>
                          ))}
                        </div>
                      ) : (
                        <span className="text-stone-400">unassigned</span>
                      )}
                    </td>
                    {/*
                      WHAT THEY PAY EACH MONTH — BOTH HALVES.

                      This column quoted `orderBillingLabel`, the hardware
                      alone, under a heading that said "Financing" beside a
                      tenant name. Read at a glance on an orders screen that is
                      a subscription figure, and it was short by the software.

                      An unpriced tenant shows the hardware label it always
                      showed and says the software is not in it, rather than
                      adding to a zero — see `useSoftwareMonthlyByTenant`.
                    */}
                    <td className="px-4 py-3 align-top">
                      {typeof software === 'number' ? (
                        <>
                          <div className="text-sm font-bold text-stone-900 tabular-nums">
                            {money(software + hardware.monthlyCents)}<span className="text-[11px] font-semibold text-stone-400">/mo</span>
                          </div>
                          <div className="text-[10.5px] text-stone-500 leading-snug">
                            Software {money(software)} · Hardware {money(hardware.monthlyCents)}
                          </div>
                          {hardware.onceCents > 0 && (
                            <div className="text-[10.5px] text-stone-400">+ {money(hardware.onceCents)} one-time</div>
                          )}
                        </>
                      ) : (
                        <>
                          <div className="text-xs text-stone-700">{orderBillingLabel(o.items)}</div>
                          <div className="text-[10.5px] text-stone-400">hardware only</div>
                        </>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {/* W5 review I8 — the order's real envelopeStatus (stamped by
                          billing-service, DISPLAY CACHE only) now renders here; an
                          honest "—" only when the order carries no envelope at all. */}
                      <span className={`text-[10px] font-semibold rounded-full px-2 py-0.5 border ${AGREEMENT_CHIP[o.envelopeStatus || ''] || 'bg-stone-100 text-stone-500 border-stone-200'}`}>
                        {o.envelopeId ? AGREEMENT_LABEL[o.envelopeStatus || ''] || o.envelopeStatus || 'unknown' : '—'}
                      </span>
                    </td>
                    {/* MERCHANT — who is working the card processing, and how
                        many of this order's payment devices are keyed to a TID.
                        Counts ASSIGNMENTS, never `status === 'approved'`: a
                        store that brought its own processor never reaches
                        approved and still ships. */}
                    <td className="px-4 py-3">
                      {(() => {
                        const app = apps[o.locationId];
                        const byModel = new Map(models.map((m) => [m.modelId, m]));
                        const payment = orderUnits.filter((u) => needsTerminalId(byModel.get(u.modelId) as any));
                        if (payment.length === 0) {
                          return <span className="text-[11px] text-stone-400">no card devices</span>;
                        }
                        /* SAME RULE AS THE GATE — a reader is settled by the
                           sheet BOUND to it, or by a TID keyed off one. This
                           column counted assignments alone, so a shippable
                           order read "0 of 1 TIDs" in amber on the list while
                           its own rail said done. */
                        const keyed = payment.filter(
                          (u) => (app?.terminalAssignments || []).some((t) => t.unitId === u.unitId)
                            || !!activeSheetFor(app?.varSheets, u.unitId),
                        ).length;
                        return (
                          <>
                            <span className={`text-[10px] font-semibold rounded px-2 py-1 ${
                              app ? MERCHANT_STATUS_CHIP[app.status] : 'bg-gray-200 text-gray-700'}`}
                            >
                              {app ? (MERCHANT_STATUS_LABEL[app.status] || app.status) : 'Not started'}
                            </span>
                            <div className={`text-[10px] mt-1.5 ${keyed === payment.length ? 'text-emerald-700' : 'text-amber-700'}`}>
                              {keyed} of {payment.length} TID{payment.length === 1 ? '' : 's'}
                            </div>
                            {app?.assignedIsoName && (
                              <div className="text-[10px] text-stone-400 mt-0.5 truncate max-w-[9rem]">{app.assignedIsoName}</div>
                            )}
                          </>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] font-semibold rounded px-2 py-1 ${ORDER_STATUS_CHIP[o.status] || 'bg-gray-200 text-gray-700'}`}>
                        {(ORDER_STATUS_LABEL[o.status] || o.status).toUpperCase()}
                      </span>
                      {o.partial && <span className="ml-1 text-[10px] font-semibold text-amber-700">PARTIAL</span>}
                    </td>
                    {/*
                      GO — OPEN THE ORDER. On every row, not just an awaiting one.

                      This was "Allocate", which fired allocation from a list
                      row. Allocating is a decision about which physical units
                      leave the shelf and what is short; it belongs on the
                      order, where `AllocateStagePanel` shows the shortfall
                      against the devices actually ordered — and it is still
                      there, unchanged. Nothing was lost by taking it off a
                      table row; the judgement simply happens where the
                      information is.

                      The row has always been clickable. The button makes that
                      affordance visible instead of hoping it is discovered.
                    */}
                    <td className="px-4 py-3 text-right align-top">
                      <button
                        onClick={(e) => { e.stopPropagation(); openOrder(o.orderId); }}
                        aria-label={`Open order ${o.orderNo ? displayOrderNumber(o.orderNo) : o.orderId}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-white text-[11px] font-semibold ml-auto hover:opacity-90"
                        style={{ background: '#DC143C' }}
                      >
                        Go <ArrowRight size={12} />
                      </button>
                    </td>
                  </tr>
                );
              })}
              {visible.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-stone-400">
                    {stage
                      ? `No orders are ${PIPELINE.find((c) => c.id === stage)?.label.toLowerCase()} right now.`
                      : 'No device orders yet.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <p className="mt-3 text-[11px] text-stone-500">
        An order can't pass <strong>Packing</strong> while its device agreement is unsigned,
        or while a card terminal on it has no <strong>TID</strong>.
        Placed {orders.length > 0 ? `${shortDate(orders[orders.length - 1]?.createdAt)} – ${shortDate(orders[0]?.createdAt)}` : '—'}.
      </p>
    </InventoryPage>
  );
};

export default OrdersPage;
